import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../lib/api';
import { useSchueler } from '../../contexts/SchuelerContext';
import { tm, wtKommaTm } from '../schueler/profil/format';

// ── Welle K1: „Braucht Aufmerksamkeit“ der Klasse — GET /api/klassen/:id/aufmerksamkeit ──
// Regeln wie „Steht an“ im Profil (Server): §68, Attest fehlt, Entschuldigung fehlt (älter als
// 3 Tage), Nachschreiben. Neu geladen, sobald sich Fehlzeiten der Klasse ändern.

export type TagStunden = { datum: string; stunden: number };
export type AufmerksamkeitKind = {
  schueler_id: number;
  unentschuldigt: number;
  paragraph68: boolean;
  attest_offen: TagStunden[];
  ohne_entschuldigung: TagStunden[];
  nachschreiben: { datum: string | null; fach: string | null; titel: string }[];
};
export type Aufmerksamkeit = { schwellen: { gelb: number; rot: number }; schuljahr: string; schueler: AufmerksamkeitKind[] };

export function useAufmerksamkeit(klasseId: number | null | undefined): { daten: Aufmerksamkeit | null; fehler: boolean } {
  const { schuelerListe } = useSchueler();
  const [daten, setDaten] = useState<Aufmerksamkeit | null>(null);
  const [fehler, setFehler] = useState(false);
  // Fehlzeiten-Stand der Klasse (patchSchueler nach dem Eintragen ändert ihn)
  const stand = useMemo(() => schuelerListe.filter(s => Number(s.klasse_id) === Number(klasseId))
    .map(s => `${s.id}:${s.fehlzeiten.stunden_gesamt}:${s.fehlzeiten.stunden_unentschuldigt}`).join('|'), [schuelerListe, klasseId]);
  useEffect(() => {
    if (!klasseId) { setDaten(null); return; }
    let aktiv = true;
    apiFetch<Aufmerksamkeit>(`/api/klassen/${klasseId}/aufmerksamkeit`)
      .then(d => { if (aktiv) { setDaten(d); setFehler(false); } })
      .catch(() => { if (aktiv) { setDaten(null); setFehler(true); } });
    return () => { aktiv = false; };
  }, [klasseId, stand]);
  return { daten, fehler };
}

export type Grund = { art: 'paragraph68' | 'attest' | 'entschuldigung' | 'nachschreiben'; ton: 'rot' | 'amber' | 'violett'; icon: string; text: string };

const stundenText = (n: number) => `${String(n).replace('.', ',')} ${n === 1 ? 'Stunde' : 'Stunden'}`;

/** Gründe eines Kindes, wichtigster zuerst (§68 rot, Attest/Entschuldigung amber, Nachschreiben violett) */
export function gruende(k: AufmerksamkeitKind | undefined): Grund[] {
  if (!k) return [];
  const aus: Grund[] = [];
  if (k.paragraph68) aus.push({ art: 'paragraph68', ton: 'rot', icon: 'warning', text: `§68: ${String(k.unentschuldigt).replace('.', ',')} Std. unentschuldigt` });
  if (k.attest_offen.length) aus.push({ art: 'attest', ton: 'amber', icon: 'description',
    text: k.attest_offen.length === 1 ? `Attest für ${tm(k.attest_offen[0].datum)} fehlt` : `${k.attest_offen.length} Atteste fehlen` });
  if (k.ohne_entschuldigung.length) aus.push({ art: 'entschuldigung', ton: 'amber', icon: 'mail',
    text: k.ohne_entschuldigung.length === 1 ? `Entschuldigung für ${tm(k.ohne_entschuldigung[0].datum)} fehlt` : `Entschuldigung fehlt für ${k.ohne_entschuldigung.length} Tage` });
  for (const n of k.nachschreiben) aus.push({ art: 'nachschreiben', ton: 'violett', icon: 'event',
    text: n.datum ? `Nachschreiben am ${wtKommaTm(n.datum)}` : `Nachschreiben${n.fach ? ` ${n.fach}` : ''}` });
  return aus;
}

/** „Mo, 19.10., 6 Stunden“ bzw. „Di, 20.10., Attest fehlt“ — der älteste offene Tag, weitere als Zahl */
export function fehltText(k: AufmerksamkeitKind): string {
  const tage = [...k.ohne_entschuldigung.map(t => ({ ...t, attest: false })), ...k.attest_offen.map(t => ({ ...t, attest: true }))]
    .sort((a, b) => a.datum.localeCompare(b.datum));
  if (!tage.length) return '';
  const t = tage[0];
  const erster = `${wtKommaTm(t.datum)}, ${t.attest ? 'Attest fehlt' : stundenText(t.stunden)}`;
  const rest = tage.length - 1;
  return rest > 0 ? `${erster} und ${rest} ${rest === 1 ? 'weiterer Tag' : 'weitere Tage'}` : erster;
}
